import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BlurLinearIcon from "@mui/icons-material/BlurLinear";
import ArticleIcon from "@mui/icons-material/Article";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import DashboardIcon from "@mui/icons-material/Dashboard";

const getTabIcon = (label, sectionKey) => {
  switch (label) {
    case "Content Page":
      return <DashboardIcon sx={{ fontSize: 16 }} />;
    case "Main Computation":
      return <ArticleIcon sx={{ fontSize: 16 }} />;
    case "Detailed P&L":
      return <MonetizationOnIcon sx={{ fontSize: 16 }} />;
    default:
      // For schedule tabs, determine icon based on schedule type
      if (
        [
          "FARegister",
          "FASummary",
          "FAAdditions",
          "FADisposals",
          "S19Schedule",
          "S19ASchedule",
          "S19BSchedule",
          "S19DSchedule",
          "S16Schedule",
          "CASummary",
        ].includes(sectionKey)
      ) {
        return <AccountBalanceIcon sx={{ fontSize: 16 }} />;
      }
      return <BlurLinearIcon sx={{ fontSize: 16 }} />;
  }
};

const StickyTabs = ({ tabs, activeTab, onTabChange, onCloseTab }) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuTab, setContextMenuTab] = useState(null);

  const handleContextMenu = (event, tabIndex) => {
    event.preventDefault();
    setContextMenuTab(tabIndex);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setContextMenuTab(null);
  };

  const handleCloseAllTabs = () => {
    for (let i = tabs.length - 1; i >= 0; i--) {
      onCloseTab(i);
    }
    handleCloseContextMenu();
  };

  const handleCloseOtherTabs = () => {
    const tabToKeep = contextMenuTab;

    for (let i = tabs.length - 1; i >= 0; i--) {
      if (i !== tabToKeep) {
        onCloseTab(i);
      }
    }
    handleCloseContextMenu();
  };

  const handleCloseTabsToTheRight = () => {
    for (let i = tabs.length - 1; i > contextMenuTab; i--) {
      onCloseTab(i);
    }
    handleCloseContextMenu();
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        backgroundColor: "background.paper",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minHeight: 55,
          bgcolor: "background.paper",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => onTabChange(event, newValue)}
          variant='scrollable'
          scrollButtons='auto'
          sx={{
            flex: 1,
            minHeight: 55,
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              backgroundColor: "primary.main",
              transition: "all 0.3s ease",
            },
            "& .MuiTab-root": {
              minHeight: 55,
              padding: "12px 16px",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "text.secondary",
              transition: "all 0.2s ease-in-out",
              "&.Mui-selected": {
                color: "primary.main",
                fontWeight: 600,
              },
              "&:hover": {
                color: "primary.main",
                backgroundColor: "action.hover",
              },
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              onContextMenu={(e) => handleContextMenu(e, index)}
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                    maxWidth: 200,
                  }}
                >
                  {getTabIcon(tab.label, tab.sectionKey)}
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "inherit",
                      fontWeight: "inherit",
                    }}
                  >
                    {tab.label}
                  </Typography>
                  <Tooltip title='Close tab'>
                    <IconButton
                      size='small'
                      onClick={(e) => {
                        e.stopPropagation();
                        onCloseTab(index);
                      }}
                      sx={{
                        ml: 0.5,
                        p: 0.5,
                        color: "inherit",
                        opacity: 0.7,
                        "&:hover": {
                          opacity: 1,
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
          ))}
        </Tabs>
        {tabs.length > 1 && (
          <Tooltip title='Tab options'>
            <IconButton
              size='small'
              onClick={(e) => handleContextMenu(e, activeTab)}
              sx={{
                ml: 1,
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <MoreHorizIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference='anchorPosition'
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          elevation: 4,
          sx: {
            minWidth: 200,
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#1E1E1E" : "#fff",
            border: "1px solid",
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "grey.200",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0px 4px 20px rgba(0,0,0,0.5)"
                : "0px 4px 16px rgba(0,0,0,0.1)",
            overflow: "hidden",
          },
        }}
        MenuListProps={{
          sx: {
            py: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={handleCloseAllTabs}
          sx={{
            py: 1,
            px: 2,
            fontSize: 14,
            "&:hover": {
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "grey.100",
            },
          }}
        >
          <CloseIcon fontSize='small' color='error' sx={{ mr: 1 }} />
          Close All Tabs
        </MenuItem>

        <MenuItem
          onClick={handleCloseOtherTabs}
          disabled={tabs.length <= 1}
          sx={{
            py: 1,
            px: 2,
            fontSize: 14,
            opacity: tabs.length <= 1 ? 0.5 : 1,
            "&:hover": {
              bgcolor:
                tabs.length <= 1
                  ? "transparent"
                  : (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "grey.100",
            },
          }}
        >
          <CloseIcon fontSize='small' sx={{ mr: 1, color: "warning.main" }} />
          Close Other Tabs
        </MenuItem>

        <MenuItem
          onClick={handleCloseTabsToTheRight}
          disabled={contextMenuTab === tabs.length - 1}
          sx={{
            py: 1,
            px: 2,
            fontSize: 14,
            opacity: contextMenuTab === tabs.length - 1 ? 0.5 : 1,
            "&:hover": {
              bgcolor:
                contextMenuTab === tabs.length - 1
                  ? "transparent"
                  : (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "grey.100",
            },
          }}
        >
          <CloseIcon fontSize='small' sx={{ mr: 1, color: "info.main" }} />
          Close Tabs to the Right
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={handleCloseContextMenu}
          sx={{
            py: 1,
            px: 2,
            fontSize: 13,
            justifyContent: "center",
            color: "text.secondary",
            "&:hover": {
              bgcolor: "transparent",
              color: "text.primary",
            },
          }}
        >
          Cancel
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default StickyTabs;
